// core
import { Component, Renderer2 } from "@angular/core";
import { BehaviorSubject, combineLatest } from "rxjs";
import { tap, shareReplay, publish } from "rxjs/operators";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";

// services
import { ToastService } from "../../services/toast.service";
import { UserCanService } from "../../services/user-can.service";

// API
import {
    UserRight,
    TerminalOperation,
    OperationClient,
    GetResourceRequest,
    ResourceClient,
    ResourceItem,
    OperationFile,
    OperationFileClient,
    FileParameter,
    FileResponse
} from "../../WebApiClient";

// models & components
import { ComponentBase } from "../../models/ComponentBase";
import { TypedModalRef } from "../../models/TypedModalRef";

// assets
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { FileDropEntry, FileSystemFileEntry } from "../file-drop/file-drop-entry";

export class optionsArr {
    public list: (string | undefined)[] = [];
}
export class selectedElement {
    public value: string | undefined = undefined;
}

export class ExtendedFile extends OperationFile {
    public thumbnail: string = '';
    public content: string = '';
    public fileInstance: File | null = null;
}

@Component({
    selector: "app-operation-add-resources",
    standalone: false,
    templateUrl: "./operation-add-resources.component.html",
    styleUrls: ["./operation-add-resources.component.scss"],
    providers: [OperationClient, ResourceClient, OperationFileClient],
})
export class OperationAddResourcesComponent extends ComponentBase {
    readonly faDelete = faTrashAlt;

    private readonly trigger = new BehaviorSubject(true);
    public isLoadingState = false;

    public combinedId: string | undefined = undefined;
    public operation: TerminalOperation | undefined = undefined;

    // Vehicles data types
    public selectedType: selectedElement = new selectedElement();
    public selectedCapacity: selectedElement = new selectedElement();
    public selectedNumber: selectedElement = new selectedElement();

    public types: optionsArr = new optionsArr();
    public capacities: optionsArr = new optionsArr();
    public numbers: optionsArr = new optionsArr();

    public vehicles: ResourceItem[] = [];
    public vehiclesRequestCompleted = false;
    public vehiclesFetched = false;
    public selectedVehicles: ResourceItem[] = [];
    
    // Files data types
    public allowedImageTypes: readonly string[] = [ 
        'image/gif',
        'image/jpeg',
        'image/png',
        'image/webp',
    ];
    public imageZoom = 1;
    public translateX = 0;
    public translateY = 0;
    private isDragging = false;
    private dragStartX = 0;
    private dragStartY = 0;
    private initialTranslateX = 0;
    private initialTranslateY = 0;
    public readonly pdfMimeType: string = 'application/pdf';
    public txtMimeTypes: readonly string[] = [
        'text/plain',
        'text/rtf',
        'text/richtext',
    ];
    public docMimeType: readonly string[] = [
        'application/rtf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.text-template',
        'application/vnd.ms-word.document.macroEnabled.12',
        'application/vnd.ms-word.template.macroEnabled.12',
    ];
    public xlsMimeType: readonly string[] = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.spreadsheet-template',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.template.macroEnabled.12',
        'application/vnd.ms-excel.addin.macroEnabled.12',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    ];
    public pptMimeType: readonly string[] = [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.openxmlformats-officedocument.presentationml.template',
        'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
        'application/vnd.oasis.opendocument.presentation',
        'application/vnd.oasis.opendocument.presentation-template',
        'application/vnd.ms-powerpoint.addin.macroEnabled.12',
        'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
        'application/vnd.ms-powerpoint.template.macroEnabled.12',
        'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
    ];

    public readonly allowedDocumentTypes: string[] = [ this.pdfMimeType, ...this.txtMimeTypes, ...this.docMimeType, ...this.xlsMimeType, ...this.pptMimeType,];
    public operationFiles: ExtendedFile[] = [];
    public fetchedFiles: ExtendedFile[] = [];
    public deletedFilesIds: string[] = [];
    
    public filesRequestCompleted = false;
    public isDragOver = false;
    public isUploading = false;

    public previewClass: string = '';
    public previewFileName: string = '';
    public previewImgSource: string = '';
    public readonly previewPdfId: string = 'pdf_preview';

    // Nav tabs
    public activeTab: number = 1;
    public navTabs: { id: number; tabName: string }[] = [
        { id: 1, tabName: 'vehicles' },
        { id: 2, tabName: 'files' },
        { id: 3, tabName: 'links' }
    ];

    public constructor(
        private readonly activeModal: NgbActiveModal,
        private operationClient: OperationClient,
        private operationFileClient: OperationFileClient,
        private readonly toastService: ToastService,
        private resourseClient: ResourceClient,
        private readonly renderer: Renderer2,
        public readonly userCan: UserCanService
    ) {
        super();
    }

    // #region Vehicles

    public sortOptions(options: optionsArr) {
        const result = [ ...options.list].sort((a, b) => {
            const numberA = parseInt(a||"");
            const numberB =  parseInt(b||"");
            if (!isNaN(numberA) && !isNaN(numberB)) {
                return numberA - numberB;
            }
            else {
                const nameA = a || "";
                const nameB =  b || "";
                return nameA.localeCompare(nameB);
            }
        });
        return result;
    }

    public updateTypes() {
        this.types.list = [...new Set(this.vehicles.filter(i => i.subType?.trim()).map(i => i.subType))];
        this.autoSelectIfOne(this.types, this.selectedType, this.onTypeChange);
    }

    public autoSelectIfOne(options:optionsArr, selectedObj: selectedElement | undefined, callbackFn: Function | null = null) {
        if (!options.list.length) {
            return;
        }
        else if (options.list.length === 1) {
            setTimeout(() => {
                if (selectedObj) {
                    selectedObj.value = options.list[0];
                    if(callbackFn)  { callbackFn.bind(this)(); }
                }
            }, 100);
        }
        else if (selectedObj) {
            options.list = this.sortOptions(options);
            selectedObj.value = undefined;
            if(callbackFn)  { callbackFn.bind(this)(); }
        }
    }

    public onTypeChange() {
        this.selectedCapacity.value = undefined;
        this.selectedNumber.value = undefined;
        this.numbers.list = [];

        const filtered = this.vehicles.filter(i => i.capacityText?.trim() && i.subType === this.selectedType.value);
        this.capacities.list = [...new Set(filtered.map(i => i.capacityText))];
        this.autoSelectIfOne(this.capacities, this.selectedCapacity, this.onCapacityChange);
    }

    public onCapacityChange() {
        this.selectedNumber.value = undefined;
        const filtered = this.vehicles.filter(i => 
            i.externalId?.trim()
            && i.subType === this.selectedType.value
            && i.capacityText === this.selectedCapacity.value
        );
        this.numbers.list = [...new Set(filtered.map(i => i.externalId))];
        this.autoSelectIfOne(this.numbers, this.selectedNumber);
        setTimeout(() => {
            document.getElementById("add_vehicle")?.focus();
        }, 250);
    }

    public addVehicle(machineId: string | undefined = undefined) {
        const selectedVehicle = machineId
            ? this.vehicles.find(i =>
                i.id === machineId
            )
            : this.vehicles.find(i =>
                i.subType === this.selectedType.value
                && i.capacityText === this.selectedCapacity.value
                && i.externalId === this.selectedNumber.value
            );
        if (selectedVehicle) {
            this.selectedType.value = undefined;
            this.selectedCapacity.value = undefined;
            this.capacities.list = [];
            this.selectedNumber.value = undefined;
            this.numbers.list = [];

            this.vehicles = this.vehicles.filter((i) => i.id !== selectedVehicle.id);
            this.updateTypes();
            this.selectedVehicles.push(selectedVehicle);
        }
    }
            
    public removeVehicle(machine: ResourceItem) {
        if (!window.confirm('Are you sure you want to delete it ?')) {return;}
        if(machine?.id) {
            this.vehicles.push(machine);
            this.updateTypes();
            this.selectedVehicles = this.selectedVehicles.filter((i) => i.id !== machine.id);
        }
    }
    // #endregion

    // #region Files

    public fetchOperationFiles() {
        if(this.operation?.id) {
            this.operationFileClient.getOperationFiles(this.operation.id).pipe(
                tap((response) => {
                    this.filesRequestCompleted = true;
                    this.fetchedFiles = response?.operationFiles as ExtendedFile[] || [];
                    if(this.fetchedFiles.length) {
                        this.operationFiles = [ ...this.fetchedFiles ];
                        this.operationFiles.forEach(i => {
                            fetch(i.url)
                                .then(res => res.blob())
                                .then(blob => {
                                    const file = new File([blob], i.name, { type: blob.type });
                                    i.fileInstance = file;
                                    if (this.isImageFile(file.type) ) {
                                        this.generateThumbnail(file).then(thumb => {
                                            i.thumbnail = thumb;
                                        });
                                    }
                                    if (this.allowedDocumentTypes.includes(file.type) ) {
                                        this.getDocumentContent(file).then(data => {
                                            i.content = data;
                                        });
                                    }
                                });
                        });
                    }
                }),
                shareReplay(1)
            ).subscribe();
        }
    }
            
    public removeFile(fileId: string) {
        if(fileId && window.confirm('Are you sure you want to delete it ?')) {
            if(this.fetchedFiles.find(i => i.fileId === fileId)) {
                this.deletedFilesIds.push(fileId);
            }
            this.operationFiles = this.operationFiles.filter((i) => i.fileId !== fileId);
        }
    }
            
    public downloadFile(fileId: string) {
        const fileItem = this.operationFiles.find(i => i.fileId === fileId);
        if(fileItem && fileItem.fileInstance) {
            const url = URL.createObjectURL(fileItem.fileInstance);

        }
    }

    public downloadAllFiles(): void {
        this.operationFiles.forEach(file => {
            this.downloadSingleFile(file);
        });
    }

    private downloadSingleFile(file: ExtendedFile): void {
        const link = document.createElement('a');

        if (file.fileInstance) {
            link.href = URL.createObjectURL(file.fileInstance);
        } else {
            link.href = file.url;
        }

        link.download = file.name || file.originalFilename || 'download';
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (file.fileInstance) {
            setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        }
    }

    async dropOperationFile(droppedFiles: FileDropEntry[]) {
        if (!this.operation?.id) return;
        const now = new Date();
        const timestamp = now.getTime();
        const operationId = this.operation?.id;
        for (let f of droppedFiles) {
            if (!f.fileEntry.isFile) {
                continue;
            }
            const fileSysEntry = f.fileEntry as FileSystemFileEntry;
            const f1 = {
                created: now,
                name: fileSysEntry.name,
                thumbnail: '',
            } as ExtendedFile;
            
            await fileSysEntry.file((file) =>
                this.allowedDocumentTypes.includes(file.type)
                    ? this.getDocumentContent(file).then(
                        content => this.addFiletoOperationFiles(f1, file, timestamp, '', content)
                    )
                    : this.generateThumbnail(file).then(
                        thumb => this.addFiletoOperationFiles(f1, file, timestamp, thumb, '')
                    )
            );
        }
    }

    private addFiletoOperationFiles(extFile: ExtendedFile, file: File, timestamp: number = 0, image_thumbnail: string = '', ducument_content: string = ''): void{
        if(extFile.name.trim()) {
            Object.assign(
                    extFile,
                    {
                        fileId: timestamp + "_" + file.name + "_" + file.lastModified.toString() + "_" + file.size.toString(),
                        thumbnail: image_thumbnail,
                        content: ducument_content,
                        originalFilename: file.name,
                        mimeType: file.type,
                        url: URL.createObjectURL(file),
                        fileInstance: file
                    }
            );
            this.operationFiles.push(extFile);
        }
    }

    private async getDocumentContent(file: File): Promise<string> {
        return new Promise((resolve) => {
            var reader = new FileReader();
            reader.onloadend = (e) => {
                resolve(reader.result as string);
            };
            reader.readAsDataURL(file);
        });
        
    }

    private async generateThumbnail(file: File): Promise<string> {
        return new Promise((resolve) => {
            const ico_width = 96;
            const ico_height = 96;
            const imageSmoothingEnabled = true;

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d')!;
                    ctx.imageSmoothingEnabled = imageSmoothingEnabled;

                    // Set canvas size
                    canvas.width = ico_width;
                    canvas.height = ico_height;

                    // Calculate scaling to maintain aspect ratio
                    const min_sideLength = Math.min(img.width, img.height);
                    const sx = (img.width  - min_sideLength) / 2;
                    const sy = (img.height  - min_sideLength) / 2;

                    ctx.drawImage(
                        img,
                        sx, sy, min_sideLength, min_sideLength,
                        0, 0, ico_width, ico_height
                    );
                    resolve(canvas.toDataURL());
                };
                img.src = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        });
    }

    public isImageFile(fileType: string): boolean {
        return this.allowedImageTypes.includes(fileType);
    }

    public isPdfFile(fileType: string): boolean {
        return fileType === this.pdfMimeType;
    }

    // PDF quick preview
    public previewPDF(content: string | undefined, filename: string = '') {
        if (content) {
            window.open(content, '_blank');

           
        }
    }

    // Image quick preview
    public previewIMG(src: string, filename: string = '') {
        if (src.trim()) {
            this.imageZoom = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.closeModal();
            this.previewClass = 'image_modal';
            this.previewImgSource = src;
            this.showModal(filename);
        }
    }

    // Modal dialog for image quick preview
    public showModal(filename: string = '') {
        const modal = document.getElementById('previewImageModal') as HTMLDialogElement | null;
        if(modal) {
            this.previewFileName = filename;
            setTimeout(() => {modal.showModal();}, 150);
        }
        else { this.closeModal(); }
    }    
    public closeModal() {
        const modal = document.getElementById('previewImageModal') as HTMLDialogElement | null;
        if(modal) {
            modal.close();
        }
        this.imageZoom = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.isDragging = false;
        this.previewImgSource = '';
        document.getElementById(this.previewPdfId)?.remove();
    }

    public startDrag(event: PointerEvent): void {
        event.preventDefault();

        if (this.imageZoom <= 1) {
            return;
        }

        this.isDragging = true;

        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;

        this.initialTranslateX = this.translateX;
        this.initialTranslateY = this.translateY;
    }

    public dragImage(event: PointerEvent): void {
        if (!this.isDragging) {
            return;
        }

        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;

        this.translateX = this.initialTranslateX + deltaX;
        this.translateY = this.initialTranslateY + deltaY;
    }

    public stopDrag(): void {
        this.isDragging = false;
    }

    public zoomIn(): void {
        this.imageZoom += 0.25;
    }

    public zoomOut(): void {
        this.imageZoom = Math.max(0.25, this.imageZoom - 0.25);
    }

    public resetZoom(): void {
        this.imageZoom = 1;
        this.translateX = 0;
        this.translateY = 0;
    }

    public onWheel(event: WheelEvent): void {
        event.preventDefault();

        if (event.deltaY < 0) {
            this.imageZoom = Math.min(5, this.imageZoom + 0.1);
        } else {
            this.imageZoom = Math.max(1, this.imageZoom - 0.1);
        }
    }

    // #endregion

    // #region Operation

    public updateSelectedVehiclesFromOperation() {
        if (this.operation?.involvedMachines?.length && this.vehicles.length) {
            this.operation.involvedMachines
                .filter(machineId => {return this.vehicles.find(i => i.id === machineId);})
                .forEach(machineId => {this.addVehicle(machineId);});
        }
    }

    // #region Update Operation
    async UpdateOperation(): Promise<void> {
        if (this.operation?.id) {
            const operationId = this.operation.id;
            // upload new files
            const filesToUpload = this.operationFiles.filter(i => !this.fetchedFiles.find(f => f.fileId === i.fileId));
            const uploadErrors: string[] = [];

            const uploadPromises = filesToUpload.map(i => {
                if(i.fileId && i.fileInstance) {
                    const fileToUpload: FileParameter = {
                        data: i.fileInstance,
                        fileName: i.name,
                    };
                    this.operationFileClient.uploadFile(operationId, i.originalFilename, fileToUpload).toPromise()
                    .catch(
                        err => { uploadErrors.push(i.name); }
                    )
                }
            });

            Promise.allSettled(uploadPromises)
            .then(() => {               
                if(uploadErrors.length) {
                    console.warn('Some files were unable to upload:', uploadErrors);
                    this.toastService.add(
                        "ERROR: Some files failed while uploading",
                        `You can check logs for a detailed list.`,
                        "warning"
                    );
                }
            });
            // delete unwanted files
            const deleteErrors: string[] = [];

            const deletePromises = this.deletedFilesIds.map(fileId => 
                this.operationFileClient.deleteOperationFile(operationId, fileId).toPromise()
                .catch(
                    () => { deleteErrors.push(fileId); }
                )
            );

            Promise.allSettled(deletePromises)
            .then(() => {               
                if(deleteErrors.length) {
                    console.warn('Some files were unable to delete:', deleteErrors);
                    this.toastService.add(
                        "ERROR: Some files were not deleted",
                        `You can check logs for a detailed list.`,
                        "warning"
                    );
                }
            });

            // update involved machines
            this.operation.involvedMachines = this.selectedVehicles.map(i => i.id || "").filter(i => i.trim());
            this.trigger.next(false);
            this.isLoadingState = true;
            try {
                await this.operationClient.updateOperationsVehiclesList(this.operation.id, this.operation.involvedMachines).toPromise();
                this.toastService.add(
                    "SUCCESS: Operation was updated",
                    // `The operation ${this.operation.externalId ? `#${this.operation.externalId}` : ''} updated successfully!`,        // TODO [4upd] -- refactor with task externalId
                    `The operation updated successfully!`,
                    "success"
                );
                this.activeModal.close();
            } catch (error) {
                this.toastService.add(
                    "ERROR: Operation was not updated",
                    "There is an error with operation update request. Operation data can not be saved!",
                    "warning"
                );
            } finally {
                this.trigger.next(true);
                this.isLoadingState = false;
            }
        } else {
            // TODO [4ref] -- add some ErrorLog or ErrorInform code here
            this.toastService.add(
                "ERROR: Cannot get any data for related Operation!",
                "Please inform Lighthouse support team about this error",
                "warning"
            );
        }
    }
    // #endregion

    // #region Modal
    private initModal(
        combinedId: string,
        operationId: string,
        tabName: string | undefined = undefined
    ): void {
        if(operationId) {
            if(combinedId) {this.combinedId = combinedId;}
            this.navTabs.forEach(i => {
                if(i.tabName === tabName) {
                    this.activeTab = i.id;
                    return;
                }
            });
            this.operation = new TerminalOperation();
            this.operation.id = operationId;
            // get operation data from DB
            const operation$ = this.operationClient.getOperation(this.operation.id).pipe(
                tap(
                    (response) => { this.operation = response; }
                ),
                shareReplay(1)
            );
            // get machines list from DB
            const resources$ = this.resourseClient
                .getResourceList(
                    { type: "1", } as GetResourceRequest
                )
                .pipe(
                    tap((response) => {
                        const resources = response?.resources || [];
                        this.vehiclesRequestCompleted = true;
                        if (resources.length) {
                            this.vehiclesFetched = true;
                            this.vehicles = [
                                ...new Set(
                                    resources
                                        .filter(i => 
                                            i.subType?.trim()
                                            && i.capacityText?.trim()
                                            && i.externalId?.trim()
                                        )
                                )
                            ];
                            this.updateTypes();
                        }
                    }),
                    shareReplay(1)
                );
            // get files list from DB
            this.fetchOperationFiles();

            // Wait while both requests will return the values
            this.subscriptions.push(
            combineLatest([resources$, operation$])
                .subscribe(
                    () => { this.updateSelectedVehiclesFromOperation(); }
                )
            );
        }
        else {
            this.activeModal.dismiss("Operation Id is not defined!");
        }
    }
    static open(
        modalService: NgbModal,
        combinedId: string,
        operationId: string,
        tabName: string = 'files'
    ): TypedModalRef<OperationAddResourcesComponent, void> {
        let modal = modalService.open(OperationAddResourcesComponent, {
            size: "lg",
            windowClass: "full-height-modal",
            backdrop: 'static',
            keyboard: false
        });
        let modalComponent = <OperationAddResourcesComponent>(
            modal.componentInstance
        );
        modalComponent.initModal(combinedId,operationId,tabName);
        return new TypedModalRef(modal);
    }
    public cancel() {
        this.activeModal.dismiss("user cancelled");
    }
}
